import { useState, useCallback, useEffect, lazy, Suspense } from 'react';
import { useTestState } from './hooks/useTestState';
import { useMetronome } from './hooks/useMetronome';
import { useAuth } from './hooks/useAuth';
import { PRE_COUNTDOWN, DEV_PRE_COUNTDOWN } from './utils/protocol';
import LandingPage from './components/screens/LandingPage';
import HowItWorksPage from './components/screens/HowItWorksPage';
import SetupScreen from './components/screens/SetupScreen';
import InstructionsScreen from './components/screens/InstructionsScreen';
import RestingHRScreen from './components/screens/RestingHRScreen';
import PreLevelScreen from './components/screens/PreLevelScreen';
import ActiveLevelScreen from './components/screens/ActiveLevelScreen';
import ResultsCarousel from './components/carousel/ResultsCarousel';
import SetPasswordPage from './components/screens/SetPasswordPage';
import PreTestConditionsScreen from './components/screens/PreTestConditionsScreen';
import ChecklistScreen from './components/screens/ChecklistScreen';
import PhoneFrame from './components/ui/PhoneFrame';
import AuthModal from './components/ui/AuthModal';

import Report from './components/Report/Report';
import { HARDCODED_REPORT } from './lib/report-data';
import SegmentPage from './components/screens/SegmentPage';
import type { SegmentSlug } from './data/segments';

const ClinicPortal = lazy(() => import('./clinic/ClinicPortal'));

export default function App() {
  const {
    state,
    screen,
    setScreen,
    updateSetup,
    setRestingHR,
    toggleDevMode,
    logLevel,
    advanceLevel,
    checkStopConditions,
    captureTestTime,
    resetTest,
    loadDemoReport,
  } = useTestState();

  const metronome = useMetronome();
  const auth = useAuth();
  const [stopReason, setStopReason] = useState('');
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<'signin' | 'signup'>('signin');
  const [authModalEmail, setAuthModalEmail] = useState('');

  // Check URL for /set-password route and ?dev=true param on mount
  useEffect(() => {
    const path = window.location.pathname;
    if (path === '/set-password') {
      setScreen('setPassword');
    }
    const params = new URLSearchParams(window.location.search);
    const demoMode = params.get('demo');
    if (demoMode === 'report' || demoMode === 'results') {
      loadDemoReport();
      setStopReason('Demo report fixture');
      return;
    }
    if (params.get('dev') === 'true' && !state.devMode) {
      toggleDevMode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTestEnd = useCallback(
    (reason: string) => {
      captureTestTime();
      setStopReason(reason);
      setScreen('results');
    },
    [setScreen, captureTestTime],
  );

  const openSignIn = useCallback((prefillEmail?: string) => {
    setAuthModalMode('signin');
    if (prefillEmail) setAuthModalEmail(prefillEmail);
    setShowAuthModal(true);
  }, []);



  // Shared auth props for all NavBars
  const authNavProps = {
    userName: auth.profile?.first_name || null,
    onSignIn: () => openSignIn(),
    onSignOut: auth.signOut,
  };

  if (window.location.hostname === 'partners.beelineclinical.com' || /^\/clinic\/?$/.test(window.location.pathname)) {
    return <Suspense fallback={<div style={{ padding: '48px' }} role="status">Opening Beeline clinic workspace…</div>}><ClinicPortal auth={auth} /></Suspense>;
  }

  // ── REPORT PAGE — standalone route ──
  const reportMatch = window.location.pathname.match(/^\/report\/(.+)$/);
  if (reportMatch) {
    return <Report data={HARDCODED_REPORT} />;
  }

  // ── SEGMENT PAGES — /clinics, /facilities, /teams ──
  const segmentMatch = window.location.pathname.match(/^\/(clinics|facilities|teams)\/?$/);
  if (segmentMatch) {
    const segment = segmentMatch[1] as SegmentSlug;
    return (
      <SegmentPage
        segment={segment}
        onLogoClick={() => { window.location.href = '/'; }}
        onHowItWorks={() => { window.location.href = '/'; setTimeout(() => setScreen('howItWorks'), 0); }}
        onStart={() => { window.location.href = '/'; }}
        authNavProps={authNavProps}
      />
    );
  }

  // ── SET PASSWORD PAGE ──
  if (screen === 'setPassword') {
    return (
      <SetPasswordPage
        onUpdatePassword={auth.updatePassword}
        onComplete={() => {
          window.history.replaceState({}, '', '/');
          setScreen('landing');
        }}
      />
    );
  }

  // ── MARKETING PAGES: full viewport width ──

  if (screen === 'landing') {
    return (
      <>
        <LandingPage
          onStart={() => setScreen('setup')}
          onHowItWorks={() => setScreen('howItWorks')}
          authNavProps={authNavProps}
        />
        {showAuthModal && (
          <AuthModal
            initialMode={authModalMode}
            initialEmail={authModalEmail}
            onClose={() => { setShowAuthModal(false); setAuthModalEmail(''); }}
            onSignUp={auth.signUp}
            onSignIn={auth.signIn}
            onResetPassword={auth.resetPassword}
          />
        )}
      </>
    );
  }

  if (screen === 'howItWorks') {
    return (
      <>
        <HowItWorksPage
          onStart={() => setScreen('setup')}
          onHowItWorks={() => setScreen('howItWorks')}
          onLogoClick={() => setScreen('landing')}
          authNavProps={authNavProps}
        />
        {showAuthModal && (
          <AuthModal
            initialMode={authModalMode}
            initialEmail={authModalEmail}
            onClose={() => { setShowAuthModal(false); setAuthModalEmail(''); }}
            onSignUp={auth.signUp}
            onSignIn={auth.signIn}
            onResetPassword={auth.resetPassword}
          />
        )}
      </>
    );
  }

  // ── SETUP: its own full-width two-column layout ──

  if (screen === 'setup') {
    return (
      <>
        <SetupScreen
          state={state}
          updateSetup={updateSetup}
          toggleDevMode={toggleDevMode}
          onBegin={() => {
            metronome.resumeAudio();
            setScreen('instructions');
          }}
          onLogoClick={() => setScreen('landing')}
          onHowItWorks={() => setScreen('howItWorks')}
          authNavProps={authNavProps}
        />
        {showAuthModal && (
          <AuthModal
            initialMode={authModalMode}
            initialEmail={authModalEmail}
            onClose={() => { setShowAuthModal(false); setAuthModalEmail(''); }}
            onSignUp={auth.signUp}
            onSignIn={auth.signIn}
            onResetPassword={auth.resetPassword}
          />
        )}
      </>
    );
  }

  // ── RESULTS: full-screen carousel experience ──

  if (screen === 'results') {
    return (
      <>
        <ResultsCarousel
          state={state}
          stopReason={stopReason}
          onNewTest={resetTest}
          isLoggedIn={!!auth.user}
          userId={auth.user?.id || null}
          userProfile={auth.profile}
          onOpenSignIn={openSignIn}
          signUpFromLead={auth.signUpFromLead}
        />
        {showAuthModal && (
          <AuthModal
            initialMode={authModalMode}
            initialEmail={authModalEmail}
            onClose={() => { setShowAuthModal(false); setAuthModalEmail(''); }}
            onSignUp={auth.signUp}
            onSignIn={auth.signIn}
            onResetPassword={auth.resetPassword}
          />
        )}
      </>
    );
  }

  // ── TEST FLOW SCREENS: inside PhoneFrame ──

  return (
    <>
      <PhoneFrame>
        {state.devMode && (
          <div
            className="font-mono"
            style={{
              fontSize: '0.6rem',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              color: 'var(--warn)',
              background: 'rgba(245,165,36,0.15)',
              borderBottom: '1px solid rgba(245,165,36,0.3)',
              textAlign: 'center',
              padding: '6px',
            }}
          >
            DEV MODE — 10s levels
          </div>
        )}
        {screen === 'instructions' && (
          <InstructionsScreen
            state={state}
            onBegin={() => { metronome.resumeAudio(); setScreen('preTestConditions'); }}
            onBack={() => setScreen('setup')}
          />
        )}
        {screen === 'preTestConditions' && (
          <PreTestConditionsScreen
            betaBlocker={state.betaBlocker}
            onContinue={() => { metronome.resumeAudio(); setScreen('checklist'); }}
            onBack={() => setScreen('instructions')}
          />
        )}
        {screen === 'checklist' && (
          <ChecklistScreen
            onBegin={() => { metronome.resumeAudio(); setScreen('restingHR'); }}
            onBack={() => setScreen('preTestConditions')}
          />
        )}
        {screen === 'restingHR' && (
          <RestingHRScreen
            onConfirm={(hr) => {
              metronome.resumeAudio();
              setRestingHR(hr);
              setScreen('preLevel');
            }}
            onSkip={() => { metronome.resumeAudio(); setScreen('preLevel'); }}
          />
        )}
        {screen === 'preLevel' && (
          <PreLevelScreen
            level={state.currentLevel}
            countdownSeconds={state.devMode ? DEV_PRE_COUNTDOWN : PRE_COUNTDOWN}
            onComplete={() => { metronome.resumeAudio(); setScreen('activeLevel'); }}
            playCountBeep={metronome.playCountBeep}
          />
        )}
        {screen === 'activeLevel' && (
          <ActiveLevelScreen
            state={state}
            startMetronome={metronome.start}
            stopMetronome={metronome.stop}
            playCountBeep={metronome.playCountBeep}
            logLevel={logLevel}
            advanceLevel={advanceLevel}
            checkStopConditions={checkStopConditions}
            onTestEnd={handleTestEnd}
          />
        )}
      </PhoneFrame>
      {showAuthModal && (
        <AuthModal
          initialMode={authModalMode}
          initialEmail={authModalEmail}
          onClose={() => { setShowAuthModal(false); setAuthModalEmail(''); }}
          onSignUp={auth.signUp}
          onSignIn={auth.signIn}
          onResetPassword={auth.resetPassword}
        />
      )}
    </>
  );
}
